import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { PHASE } from '@rsos/constants/locationTypes';
import useIsGoogleMapsFeatureFlagEnhanced from '@rsos/hooks/useIsGoogleMapsFeatureFlagEnhanced';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import InfoWindowContent from '../../InfoWindow/InfoWindowContent';
import { createWebMercatorViewport } from '../utils';
import { normalizeLocation } from '../utils';
import { StyledPopoverCaret, StyledCallPopover } from './MapPopover.styles';

const MemoizedIncidentPopover = React.memo(({ x, y, data, callStatus }) => {
  const coord = {
    x: x || 0,
    y: y || 0,
  };
  const rspDeckGL = useIsGoogleMapsFeatureFlagEnhanced();

  if (data?.callData?.dataType === 'call') {
    let normalizedY = coord.y;

    if (rspDeckGL && !callStatus) {
      // If a call doesn't have a call status, the popover position is too high up
      normalizedY += 20;
    }
    return (
      <StyledCallPopover x={coord.x} y={normalizedY}>
        <InfoWindowContent
          type={EMERGENCY_CALLS}
          data={data?.callData}
          callStatus={callStatus}
        />
        <StyledPopoverCaret isGoogleMap />
      </StyledCallPopover>
    );
  }
  return <></>;
});

const IncidentMapPopover = ({ overlay }) => {
  const [popoverPosition, setPopoverPosition] = useState(null);
  const [popoverData, setPopoverData] = useState(null);
  const basemap = useSelector(state => state.sinatra.ui.basemap);
  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const selectedAirAmbulance = useSelector(
    state => state.airAmbulance.selectedAirAmbulance,
  );
  const incidentPopover = useSelector(
    state => state.mapPopover.incidentPopover,
  );

  const callQueue = useSelector(state => state.emergencyCalls.callQueue);
  const hoveredID = useSelector(state => state.hoveredIncident.hoveredID);

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );
  const hasCallStatusAccess = useSelector(state =>
    checkCallStatusAccessSelector({ psaps: state.psaps }),
  );
  const callStatus = getMostRecentCallStatus({
    callerID: popoverData?.callData?.caller_id,
    callQueue: callQueue,
    hasAniAliAccess,
    hasCallStatusAccess,
  });

  const rspDeckGL = useIsGoogleMapsFeatureFlagEnhanced();

  const incidentPopoverNormalized = useMemo(() => {
    if (incidentPopover) {
      return incidentPopover;
    } else {
      const isCallHovered = !!callQueue[hoveredID];
      if (!selectedAirAmbulance) {
        if (isCallHovered) {
          // Only show popover for a call when in all view or when it's selected
          if (!selectedCallerID || selectedCallerID === hoveredID) {
            const { mostRecentLocation, mostRecentSecondaryLocation } =
              getMostRecentLocations(callQueue[hoveredID]);
            const normalizedLocation = normalizeLocation(mostRecentLocation);
            const isValidCoords = isValidCoordinates(normalizedLocation);

            // CALLS - with ONLY secondary location
            const callWithOnlySecondaryLoc =
              !mostRecentLocation &&
              mostRecentSecondaryLocation?.class_of_service_category?.includes(
                PHASE,
              );

            if (
              callWithOnlySecondaryLoc &&
              (!selectedCallerID || selectedCallerID === hoveredID)
            ) {
              return mostRecentSecondaryLocation;
            } else if (rspDeckGL && isValidCoords) {
              // TODO: remove rspDeckGL condition and return mostRecentLocation and
              // return null after deckgl feature flag is removed and production ready

              // return most recent location for deckgl.
              return normalizedLocation;
            }
            return null;
          }

          return null;
        } else {
          // ALERTS - no action for now
          return null;
        }
      }
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentPopover, hoveredID, callQueue]);

  /**
   * Repositions the popover based on the current viewState.
   * @param {Object} viewState
   */
  const reposition = useCallback(
    (viewState, incidentPopoverNormalized) => {
      const viewport = createWebMercatorViewport(viewState, basemap);
      if (incidentPopoverNormalized) {
        let callLocPosition = [
          incidentPopoverNormalized.longitude,
          incidentPopoverNormalized.latitude,
        ];
        const [x, y] = viewport.project(callLocPosition);

        setPopoverData({
          callData: incidentPopoverNormalized, // wrapped in {callData:...} when passing to CallPopover
          x,
          y,
        });
        setPopoverPosition({ x, y });
      }
    },
    [setPopoverPosition, basemap],
  );

  // Reposition whenever the popover data changes.
  useEffect(() => {
    if (overlay && incidentPopoverNormalized) {
      const viewState = overlay.getViewState();
      reposition(viewState, incidentPopoverNormalized);
    }

    if (!incidentPopoverNormalized) {
      setPopoverPosition(null);
      setPopoverData(null);
    }
  }, [overlay, incidentPopoverNormalized, reposition]);

  return (
    <>
      {mapProvider === GOOGLE && popoverPosition && popoverData && (
        <MemoizedIncidentPopover
          x={popoverPosition?.x}
          y={popoverPosition?.y}
          data={popoverData}
          callStatus={callStatus}
        />
      )}
    </>
  );
};

export default IncidentMapPopover;
